/**
 * What the site photo viewer's stage shows for the photo on screen (#665
 * follow-up), as one pure decision so its combinations can be tested.
 *
 * The viewer has two sources for a photo: Graph's 800 px thumbnail, which
 * comes first, and the stored file, fetched after a short dwell. It shows
 * the file when it has one, else the thumbnail.
 *
 * `fill` sizes the image by the stage rather than by its own pixels. That is
 * what lets the thumbnail and the file occupy the same box, so swapping one
 * for the other doesn't move a zoomed photo. It applies while a file is
 * coming or shown. Once a photo settles on its thumbnail it keeps its own
 * size, so it isn't stretched soft, with one exception: if it was filling
 * the stage and the reader is zoomed in (a fetch that failed mid-zoom), it
 * keeps filling until they zoom back out, so the box never changes size
 * under a zoom in either direction.
 */
export type ViewerImage =
  | { kind: 'loading' }
  | { kind: 'none' }
  | { kind: 'image'; src: string; fill: boolean };

export function viewerImage(o: {
  /** The stored file's object URL; null = can't be drawn; undefined = none (yet). */
  file: string | null | undefined;
  /** A file fetch is still to come or under way for this photo. */
  filePending: boolean;
  thumb: string | null | undefined;
  thumbLoading: boolean;
  /** The reader is zoomed in (scale above 1). */
  zoomed: boolean;
  /** This photo was filling the stage at the last render. */
  wasFilling: boolean;
}): ViewerImage {
  const src = o.file ?? o.thumb ?? null;
  if (!src) return o.thumbLoading || o.filePending ? { kind: 'loading' } : { kind: 'none' };
  // Zoomed alone is not enough: a photo at its own size must not grow when
  // the reader starts to zoom into it (round 5 of the #665 follow-up).
  const holdFill = o.zoomed && o.wasFilling;
  return { kind: 'image', src, fill: !!o.file || o.filePending || holdFill };
}
